import { RelationEditor } from '@framework/form';
import { Feature } from 'ol';
import { extend as extendExtent } from 'ol/extent';
import { GeoJSON } from 'ol/format';
import VectorSource from 'ol/source/Vector';
import { useRef } from 'react';
import { IMapWidget } from '../../IMapWidget';
import { MapCadastralAreaItem } from '../../createMapSchema';
import { IMapCompetenceIntersectionItem } from './IMapCompetenceIntersectionItem';
import { computeFeaturesIntersection } from './computeFeaturesIntersection';
import { createIntersectionVectorSource } from './createIntersectionVectorSource';
import { sendMapIntersectionCompetenceMessage } from './sendMapIntersectionCompetenceMessage';

interface IMapIntersectionManagerOptions {
  /** Sorgente delle feature disegnate */
  featuresSource: React.RefObject<VectorSource>;
  /** Editor */
  editor: RelationEditor<MapCadastralAreaItem>;
  /** Opzioni del widget */
  widget: IMapWidget;
}

// Conosciuta anche come 102100 (ESRI) o 3857 (EPSG)
const geoJSON = new GeoJSON({ featureProjection: 'EPSG:3857' });

/**
 * Si occupa di gestire il calcolo delle intersezioni tra le geometrie disegnate
 * e quelle presenti nel layer di intersezione.
 * Viene utilizzato per:
 * 1. Calcolare le intersezioni tra le geometrie disegnate e quelle delle particelle
 * 2. Calcolare le competenze a partire dalle intersezioni con un layer di competenze
 */
export function useMapIntersectionManager(
  options: IMapIntersectionManagerOptions,
) {
  const { featuresSource, editor, widget } = options;

  const intersectionSource = useRef(
    createIntersectionVectorSource(widget.intersection),
  );

  const competenceSource = useRef(
    createIntersectionVectorSource(widget.competence),
  );

  /**
   * Ricalcola le intersezioni tra le geometrie disegnate e quelle presenti nel
   * layer di intersezione.
   * Provvede a caricare le geometrie da intersecare direttamente dal layer di
   * intersezione configurato nel widget.
   */
  const computeAndStoreIntersections = () => {
    const intersectionItems: MapCadastralAreaItem[] = [];
    const featuresExtent = featuresSource.current!.getExtent();

    computeFeaturesIntersection(
      featuresSource.current!,
      intersectionSource.current,
      (intersectable, intersectionFeature, area) => {
        // Ignoriamo le intersezioni con area minore della tolleranza
        if (area <= (widget.intersection?.areaTolerance ?? 0.05)) return;

        intersectionItems.push({
          area,
          isManual: false,
          cadastralSection: intersectable.getProperties()?.SEZIONE,
          cadastralParticle: intersectable.getProperties()?.NUMERO,
          castralMunicipality: intersectable.getProperties()?.COMUNE,
          cadastralSheet: intersectable.getProperties()?.FOGLIO,
          feature: geoJSON.writeFeature(intersectionFeature),
          affectedArea: area,
        });
      },
    );

    console.log(`[Map.intersections] Ottenute ${intersectionItems.length} geometrie dall'intersezione`); // prettier-ignore
    editor.replace(intersectionItems);

    // 4. Rimuoviamo le feature caricate dal layer di intersezione per evitare
    // problemi di memoria e di cache.
    intersectionSource.current.clear();
    intersectionSource.current.removeLoadedExtent(featuresExtent);
  };

  /**
   * Individua le competenze a partire dalle intersezioni calcolate.
   */
  function computeAndNotifyCompetence() {
    const featuresExtent = featuresSource.current!.getExtent();
    const competenceItems: IMapCompetenceIntersectionItem[] = [];

    computeFeaturesIntersection(
      featuresSource.current!,
      competenceSource.current,
      (intersectable, intersectionFeature, area) => {
        // Ignoriamo le intersezioni con area minore della tolleranza
        if (area <= 0.05) return;

        const code =
          intersectable.getProperties()?.[
            widget.competence?.propertyName ?? ''
          ];

        competenceItems.push({
          affectedArea: area,
          competence: code ? String(code) : null,
        });
      },
    );

    // 4. Inviamo il messaggio di competenza riconosciuta, se presente
    sendMapIntersectionCompetenceMessage(widget, competenceItems);

    competenceSource.current.clear();
    competenceSource.current.removeLoadedExtent(featuresExtent);
  }

  // Aggiungiamo un listener per gestire l'evento di caricamento delle feature,
  // che equivale a quando l'utente ha finito di disegnare la geometria.
  const initIntersectionManager = () => {
    intersectionSource.current.on('featuresloadend', event => {
      console.log('[Map.intersection] feature caricate dal layer');
      computeAndStoreIntersections();
    });

    competenceSource.current.on('featuresloadend', event => {
      console.log('[Map.competence] feature caricate dal layer');
      computeAndNotifyCompetence();
    });
  };

  /**
   * Trigger per il calcolo delle intersezioni.
   * @param feature Feature aggiunta e/o modificata, necessaria per calcolare
   *                correttamente l'estensione delle geometrie.
   */
  const triggerIntersections = (feature?: Feature) => {
    // 1. Calcoliamo l'estensione della geometria disegnata
    const featuresExtent = feature
      ? extendExtent(
          featuresSource.current!.getExtent(),
          feature.getGeometry()!.getExtent(),
        )
      : featuresSource.current!.getExtent();

    // 2. Aggiorniamo la sorgente di intersezione
    if (widget.intersection?.enabled) {
      intersectionSource.current.loadFeatures(
        featuresExtent,
        1, // resolution
        featuresSource.current!.getProjection()!,
      );
    }

    // 3. Aggiorniamo la sorgente di competenze
    if (widget.competence?.enabled) {
      competenceSource.current.loadFeatures(
        featuresExtent,
        1, // resolution
        featuresSource.current!.getProjection()!,
      );
    }
  };

  return {
    triggerIntersections,
    initIntersectionManager,
  };
}
